<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProgressStore } from "@/stores/progressStore.js";
import { useSessionStore } from "@/stores/sessionStore.js";
import { useSkinStore } from "@/stores/skinStore.js";
import { useSettingsStore } from "@/stores/settingsStore.js";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useScenicCollectionStore } from "@/stores/scenicCollectionStore.js";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import { gameRuntimeBridge } from "@/game/bridge/runtimeBridge.js";

const route = useRoute();
const router = useRouter();
const emit = defineEmits(["route-ready"]);
const progressStore = useProgressStore();
const sessionStore = useSessionStore();
const skinStore = useSkinStore();
const settingsStore = useSettingsStore();
const languageStore = useLanguageStore();
const scenicCollectionStore = useScenicCollectionStore();
const starMapStore = useCustomStarMapStore();

const runtimeReady = ref(false);
let lastStartedKey = "";
let routeWasGame = false;
let removeGameRouteGuard = null;
let activeWorkshopPlay = null;

function getRuntimeScreen() {
  return String(
    gameRuntimeBridge.getSnapshot()?.screen
    || ""
  );
}


function pauseRuntimeIfPlaying() {
  if (!runtimeReady.value) {
    return false;
  }

  if (getRuntimeScreen() !== "playing") {
    return false;
  }

  return Boolean(
    gameRuntimeBridge.pause()
  );
}


function handleVisibilityChange() {
  if (
    document.visibilityState
    !== "hidden"
  ) {
    return;
  }

  pauseRuntimeIfPlaying();
}


function handlePageHide() {
  pauseRuntimeIfPlaying();
}


function guardGameRouteLeave(
  to,
  from
) {
  if (
    from.name !== "game"
    || to.name === "game"
    || !runtimeReady.value
  ) {
    return true;
  }

  const screen =
    getRuntimeScreen();

  if (screen === "playing") {
    gameRuntimeBridge.pause();

    return false;
  }

  if (screen === "paused") {
    return false;
  }

  return true;
}


async function authorizeFirstLaunch() {
  if (
    route.query.map === "custom-star"
    || !sessionStore.backendEnabled
  ) {
    return true;
  }

  try {
    const result =
      await sessionStore.beginActivePlay();

    return Boolean(
      result?.started
    );
  } catch (error) {
    console.warn(
      "Backend begin-play failed.",
      error
    );

    try {
      await sessionStore.refreshHome();
    } catch {
      // Keep the prepared play on screen.
    }

    return false;
  }
}


let backendSkinHydrated = false;

async function startRouteLevel(force = false) {
  if (!runtimeReady.value || route.name !== "game") return;

  if (!languageStore.initialized) {
    try {
      await languageStore.initialize();
    } catch (error) {
      console.warn("Backend language sync before gameplay failed.", error);
    }
  }

  /* BACKEND_SKIN_HYDRATE_V1_START */
  if (!backendSkinHydrated) {
    try {
      await skinStore.refresh();
      backendSkinHydrated = true;
    } catch (error) {
      console.warn(
        "Backend skin sync before game failed.",
        error
      );
    }
  }
  /* BACKEND_SKIN_HYDRATE_V1_END */


  const level = Math.max(1, Number(route.params.level) || 1);
  const key = `${level}:${route.query.entry || "continue"}:${route.fullPath}`;
  if (!force && key === lastStartedKey) return;
  lastStartedKey = key;

  const preloadedSnapshot = gameRuntimeBridge.getSnapshot();
  if (
    route.query.preloaded === "1"
    && preloadedSnapshot?.screen === "playing"
    && Number(preloadedSnapshot?.level) === level
  ) {
    emit("route-ready");
    return;
  }

  let started = false;
  if (route.query.map === "custom-star") {
    const mapId = String(route.query.mapId || "");
    const nodeLevel = Number(route.query.node) || level;
    const shareCode = String(route.query.shareCode || "");
    let map = starMapStore.getMap(mapId);

    try {
      if (starMapStore.backendEnabled) {
        const play = await starMapStore.startRemotePlay(
          mapId,
          nodeLevel,
          { shareCode }
        );

        activeWorkshopPlay = {
          mapId,
          nodeLevel,
          playId: String(play?.playId || ""),
          shareCode
        };

        map = starMapStore.getMap(mapId);
        const node = starMapStore.getNode(mapId, nodeLevel);

        if (!play?.rows || !node?.levelData?.rows || !activeWorkshopPlay.playId) {
          throw new Error("Workshop start payload incomplete.");
        }

        started = await gameRuntimeBridge.startCustomLevel({
          rows: play.rows,
          customColors: play.customColors || {},
          mapId,
          nodeLevel,
          mapName: play.mapName || map?.name || "",
          playId: activeWorkshopPlay.playId,
          attemptCount: play.attemptCount
        }, { consumeEnergy: false });
      } else {
        starMapStore.refresh();
        map = starMapStore.getMap(mapId);
        const node = starMapStore.getNode(mapId, nodeLevel);

        if (!map || !node?.levelData?.rows) {
          throw new Error("Local workshop level unavailable.");
        }

        activeWorkshopPlay = null;
        started = await gameRuntimeBridge.startCustomLevel({
          rows: node.levelData.rows,
          customColors: node.levelData.customColors || {},
          mapId,
          nodeLevel: node.level,
          mapName: map.name
        }, { consumeEnergy: false });
      }
    } catch (error) {
      console.warn("Workshop start failed.", error);
      activeWorkshopPlay = null;
      lastStartedKey = "";
      await router.replace(
        mapId
          ? {
              name: "custom-map-view",
              params: { mapId },
              query: shareCode ? { shareCode, mode: "play" } : {}
            }
          : { name: "workshop" }
      );
      return;
    }
  } else {
    started = await gameRuntimeBridge.startLevel(level, { consumeEnergy: false });
  }

  if (started === false) {
    lastStartedKey = "";
    if (route.query.map === "custom-star" && route.query.mapId) {
      await router.replace({ name: "custom-map-view", params: { mapId: String(route.query.mapId) } });
    } else {
      await router.replace({ name: "home" });
    }
    return;
  }

  if (route.query.preview === "result") {
    gameRuntimeBridge.previewClearResult();
  }

  emit("route-ready");
}

async function handleNavigate(event) {
  const target = event.detail?.to;
  if (route.query.map === "custom-star" && ["home", "levels"].includes(target)) {
    const mapId = String(route.query.mapId || "");
    if (!mapId) {
      await router.push({ name: "workshop" });
      return;
    }
    await router.push({
      name: "custom-map-view",
      params: { mapId },
      query: {
        from: "workshop",
        mode: "play",
        ...(route.query.shareCode ? { shareCode: String(route.query.shareCode) } : {}),
        ...(route.query.access === "public" ? { access: "public" } : {})
      }
    });
    return;
  }
  if (target === "levels") {
    // 游戏左上角返回键按进入来源返回：
    // 主页继续游戏进入（continue）回主页；回响地图选关进入（history）回回响地图。
    await router.push(
      route.query.entry === "history"
        ? { name: "levels" }
        : { name: "home" }
    );
  }
  else if (target === "settings") await router.push({ name: "settings" });
  else await router.push({ name: "home" });
}

async function handleFinished(event) {
  const result =
    event.detail?.result;

  if (
    route.query.map === "custom-star"
    && ["passed", "failed", "exited"].includes(result)
  ) {
    const mapId = String(
      event.detail?.mapId
      || route.query.mapId
      || activeWorkshopPlay?.mapId
      || ""
    );

    const nodeLevel =
      Number(event.detail?.nodeLevel)
      || Number(route.query.node)
      || activeWorkshopPlay?.nodeLevel
      || Number(route.params.level);

    const shareCode = String(
      route.query.shareCode
      || activeWorkshopPlay?.shareCode
      || ""
    );

    if (starMapStore.backendEnabled && activeWorkshopPlay?.playId) {
      try {
        const finishResult = await starMapStore.finishRemotePlay(
          mapId,
          nodeLevel,
          {
            playId: activeWorkshopPlay.playId,
            result,
            score: Number(event.detail?.score) || 0,
            ...(shareCode ? { shareCode } : {})
          }
        );

        activeWorkshopPlay = null;

        const progress = await starMapStore.loadRemoteProgress(
          mapId,
          { shareCode }
        );

        if (
          result === "passed"
          && (progress?.mapCompleted || finishResult?.mapCompleted)
        ) {
          await router.replace({
            name: "custom-map-complete",
            params: { mapId },
            query: shareCode ? { shareCode } : {}
          });
          return;
        }
      } catch (error) {
        console.warn("Workshop finish sync failed.", error);
      }
    } else if (!starMapStore.backendEnabled && result === "passed") {
      starMapStore.markLit(nodeLevel, mapId);

      const completedMap = starMapStore.getMap(mapId);
      const allLit = Boolean(
        completedMap?.nodes?.length
        && completedMap.nodes.every(node =>
          starMapStore.isLit(node.level, mapId)
        )
      );

      if (allLit) {
        await router.replace({
          name: "custom-map-complete",
          params: { mapId }
        });
        return;
      }
    }
  }

  if (
    route.query.map !== "custom-star"
    && sessionStore.backendEnabled
    && [
      "passed",
      "failed",
      "exited"
    ].includes(result)
  ) {
    try {
      const finishResult =
        await sessionStore.finishActivePlay({
          result,
          score: event.detail?.score,
          fragmentCollected: Boolean(
            event.detail
              ?.fragmentCollected
          )
        });

      gameRuntimeBridge.setEnergy({
        current:
          sessionStore.home?.energy
          ?? finishResult?.energy,

        max:
          sessionStore.home?.energy_max
          ?? finishResult?.energy_max,

        nextRecoveryAt:
          sessionStore.home
            ?.energy_next_recovery_at
          ?? null,

        reward:
          finishResult?.energy_reward
          ?? 0
      });

      gameRuntimeBridge.setFragmentReward(
        finishResult?.fragment_reward
        ?? 0
      );

      gameRuntimeBridge.setClearRewards(
        finishResult ?? {}
      );

      await scenicCollectionStore.refresh();
    } catch (error) {
      console.warn(
        "Backend finish-play sync failed.",
        error
      );
    }
  }
}

async function handleNext(event) {
  const nextLevel = Math.max(1, Number(event.detail?.nextLevel) || 1);
  if (route.query.map === "custom-star") {
    const mapId = String(route.query.mapId || "");
    const shareCode = String(route.query.shareCode || "");
    const map = starMapStore.getMap(mapId);
    const nextNode = starMapStore.getNode(mapId, nextLevel);
    const builtLevels = new Set(
      Array.isArray(map?.builtLevels)
        ? map.builtLevels.map(Number)
        : []
    );

    if (!map || !nextNode) {
      await router.replace(
        mapId
          ? {
              name: "custom-map-view",
              params: { mapId },
              query: shareCode ? { shareCode, mode: "play" } : {}
            }
          : { name: "workshop" }
      );
      return;
    }

    if (
      nextNode.levelData?.rows
      || builtLevels.has(Number(nextNode.level))
    ) {
      await router.replace({
        name: "game",
        params: { level: nextNode.level },
        query: {
          entry: "custom-star",
          map: "custom-star",
          mapId,
          node: nextNode.level,
          ...(shareCode ? { shareCode } : {})
        }
      });
    } else if (map.accessMode === "owner") {
      await router.replace({
        name: "custom-level-editor",
        params: { mapId, level: nextNode.level }
      });
    } else {
      await router.replace({
        name: "custom-map-view",
        params: { mapId },
        query: shareCode
          ? { shareCode, mode: "play" }
          : { access: "public", mode: "play" }
      });
    }
    return;
  }
  if (sessionStore.backendEnabled) {
    try {
      const entry = route.query.entry === "history" ? "history" : "continue";
      const play = entry === "history"
        ? await sessionStore.startHistory(nextLevel)
        : await sessionStore.startCampaign();
      const level = Number(play?.level_no) || nextLevel;
      if (level === Number(route.params.level) && entry === String(route.query.entry || "continue")) {
        const started = await gameRuntimeBridge.startLevel(level, { consumeEnergy: false });
        if (started === false) await router.replace({ name: "home" });
      } else {
        await router.replace({ name: "game", params: { level }, query: { entry } });
      }
      return;
    } catch (error) {
      console.warn("Backend continue-level failed.", error);
      return;
    }
  }
  await router.replace({ name: "game", params: { level: nextLevel }, query: { entry: "continue" } });
}

async function handleLanguageRequested(event) {
  const requestedLanguage = String(event.detail?.language || "").trim();
  if (!requestedLanguage) return;

  try {
    await languageStore.setLanguage(requestedLanguage);
  } catch (error) {
    // setLanguage keeps the selected UI language even when persistence is temporarily unavailable.
    console.warn("Language preference persistence failed.", error);
  }
}

async function handleRetry(event) {
  const level = Math.max(1, Number(event.detail?.level) || Number(route.params.level) || 1);
  if (route.query.map === "custom-star") {
    activeWorkshopPlay = null;
    lastStartedKey = "";
    await startRouteLevel(true);
    return;
  }
  if (sessionStore.backendEnabled) {
    try {
      const entry = route.query.entry === "history" ? "history" : "continue";
      const play = entry === "history"
        ? await sessionStore.startHistory(level)
        : await sessionStore.startCampaign();
      const resolvedLevel = Number(play?.level_no) || level;
      if (resolvedLevel !== Number(route.params.level)) {
        await router.replace({ name: "game", params: { level: resolvedLevel }, query: { entry } });
      } else {
        const started = await gameRuntimeBridge.startLevel(resolvedLevel, { consumeEnergy: false });
        if (started === false) await router.replace({ name: "home" });
      }
      return;
    } catch (error) {
      console.warn("Backend retry failed.", error);
      return;
    }
  }
  const started = await gameRuntimeBridge.startLevel(level, { consumeEnergy: false });
  if (started === false) await router.replace({ name: "home" });
}

onMounted(async () => {
  const anonymousSharedBrowse = route.name === "shared-map";
  const readyPromise = new Promise(resolve => {
    window.addEventListener("bricksbeater:runtime-ready", resolve, { once: true });
  });

  window.addEventListener("bricksbeater:navigate", handleNavigate);
  window.addEventListener("bricksbeater:level-finished", handleFinished);
  window.addEventListener("bricksbeater:request-next-level", handleNext);
  window.addEventListener("bricksbeater:request-retry", handleRetry);
  window.addEventListener("bricksbeater:language-requested", handleLanguageRequested);

  document.addEventListener(
    "visibilitychange",
    handleVisibilityChange
  );

  window.addEventListener(
    "pagehide",
    handlePageHide
  );

  removeGameRouteGuard =
    router.beforeEach(
      guardGameRouteLeave
    );

  /*
   * BACKEND_PREFETCH_BEFORE_RUNTIME_V1
   *
   * Fetch account-owned state before the
   * Runtime module exists.
   *
   * This prevents the first rendered frame
   * from showing temporary default energy
   * or skill inventory.
   */
  if (sessionStore.backendEnabled && !anonymousSharedBrowse) {
    try {
      await sessionStore.refreshHome();

      window.__BRICKSBEATER_BACKEND_HOME__ = {
        ...(sessionStore.home || {})
      };
    } catch (error) {
      console.warn(
        "Backend Home prefetch before Runtime failed.",
        error
      );
    }
  }

  await import("@/game/runtime/gameRuntime.js");
  await readyPromise;
  runtimeReady.value = true;

  try {
    if (!anonymousSharedBrowse) {
      await languageStore.initialize();
    }
  } catch (error) {
    console.warn(
      "Backend language sync failed.",
      error
    );
  }

  gameRuntimeBridge.setLanguage(
    languageStore.language
  );

  /*
   * BACKEND_BOOTSTRAP_APPLY_V1
   *
   * Runtime already initialized from this same
   * backend snapshot. Re-apply through the bridge
   * only to keep every runtime subsystem aligned.
   * No second HTTP request is required.
   */
  if (
    sessionStore.backendEnabled
    && sessionStore.home
  ) {
    gameRuntimeBridge.setEnergy({
      current:
        sessionStore.home.energy,
      max:
        sessionStore.home.energy_max,
      nextRecoveryAt:
        sessionStore.home
          .energy_next_recovery_at
        ?? null
    });

    gameRuntimeBridge.setSkillInventory(
      sessionStore.home
    );
  }

  delete window
    .__BRICKSBEATER_BACKEND_HOME__;

  gameRuntimeBridge.setBeforeFirstLaunch(
    authorizeFirstLaunch
  );

  /* BACKEND_SKILL_USE_V1 */
  gameRuntimeBridge.setBeforeSkillUse(
    skillType =>
      sessionStore
        .useActivePlaySkill(
          skillType
        )
  );

  gameRuntimeBridge.setSkillInventory(
    sessionStore.activePlay
    || sessionStore.home
    || {}
  );

  try {
    await settingsStore.refresh();
  } catch (error) {
    console.warn("Backend settings sync failed.", error);

    if (route.name === "game") {
      await router.replace({ name: "home" });
      return;
    }
  }

  try {
    await scenicCollectionStore.refresh();
  } catch (error) {
    console.warn(
      "Backend scenic map sync failed.",
      error
    );
  }

  routeWasGame = route.name === "game";
  await startRouteLevel(true);
});

watch(
  () => languageStore.language,
  language => {
    if (runtimeReady.value) {
      gameRuntimeBridge.setLanguage(
        language
      );
    }
  }
);

watch(
  () => [route.name, route.params.level, route.query.entry, route.query.map, route.query.mapId, route.query.node, route.query.shareCode, route.query.access],
  async ([name], [previousName] = []) => {
    const isGame = name === "game";
    if (runtimeReady.value && previousName === "game" && !isGame) {
      gameRuntimeBridge.leaveGame({ notify: true });
      lastStartedKey = "";
    }
    routeWasGame = isGame;
    if (isGame) await startRouteLevel();
  }
);

onBeforeUnmount(() => {
  gameRuntimeBridge.setBeforeFirstLaunch(
    null
  );

  gameRuntimeBridge.setBeforeSkillUse(
    null
  );
  window.removeEventListener("bricksbeater:navigate", handleNavigate);
  window.removeEventListener("bricksbeater:level-finished", handleFinished);
  window.removeEventListener("bricksbeater:request-next-level", handleNext);
  window.removeEventListener("bricksbeater:request-retry", handleRetry);
  window.removeEventListener("bricksbeater:language-requested", handleLanguageRequested);

  document.removeEventListener(
    "visibilitychange",
    handleVisibilityChange
  );

  window.removeEventListener(
    "pagehide",
    handlePageHide
  );

  if (
    typeof removeGameRouteGuard
    === "function"
  ) {
    removeGameRouteGuard();
    removeGameRouteGuard = null;
  }
  activeWorkshopPlay = null;
  if (routeWasGame) gameRuntimeBridge.leaveGame({ notify: true });
});
</script>

<template>
  <section class="game-canvas-host">
    <div class="game-runtime-main">
      <canvas id="gameCanvas" tabindex="0">{{ languageStore.t('canvasUnsupported') }}</canvas>
      <p id="status">{{ languageStore.t('loadingLevel') }}</p>
      <p class="help">{{ languageStore.t('gameHelp') }}</p>
    </div>
  </section>
</template>

<style scoped>
</style>
